(() => {
  if (window.TeamsDemoModal) {
    window.TeamsDemoModal.refresh();
    return;
  }

  const controllerSelector = '[data-teams-demo-controller]';
  const triggerSelector = '[data-teams-demo-trigger]';
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'iframe',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeModal = null;
  let activeTrigger = null;
  let closeTimer = null;
  let lockedScrollY = 0;
  let bodyStyles = null;

  const getController = () => document.querySelector(controllerSelector);

  const getConfig = () => {
    const controller = getController();

    if (!controller) {
      return null;
    }

    return {
      controller,
      mode: controller.dataset.openMode || 'modal',
      bookingUrl: controller.dataset.bookingUrl,
    };
  };

  const lockPage = () => {
    if (bodyStyles) {
      return;
    }

    lockedScrollY = window.scrollY;
    bodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };

    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    if (scrollbarWidth) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  };

  const unlockPage = () => {
    if (!bodyStyles) {
      return;
    }

    Object.assign(document.body.style, bodyStyles);
    window.scrollTo(0, lockedScrollY);
    bodyStyles = null;
  };

  const getFocusableElements = (modal) => Array.from(modal.querySelectorAll(focusableSelector)).filter((element) => {
    return !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true';
  });

  const loadFrame = (modal) => {
    const frame = modal.querySelector('[data-teams-demo-frame]');

    if (!frame || frame.hasAttribute('src')) {
      return;
    }

    frame.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });
    frame.src = frame.dataset.src;
  };

  const openModal = (modal, trigger) => {
    if (activeModal) {
      return;
    }

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    activeModal = modal;
    activeTrigger = trigger;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    loadFrame(modal);
    lockPage();

    window.requestAnimationFrame(() => {
      modal.classList.add('is-open');
      const focusTarget = modal.querySelector('.teams-demo-modal__close') || modal.querySelector('.teams-demo-modal__panel');
      focusTarget?.focus({ preventScroll: true });
    });
  };

  const closeModal = () => {
    if (!activeModal) {
      return;
    }

    const modal = activeModal;
    const trigger = activeTrigger;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    closeTimer = window.setTimeout(() => {
      if (modal.classList.contains('is-open')) {
        return;
      }

      modal.hidden = true;
      activeModal = null;
      activeTrigger = null;
      closeTimer = null;
      unlockPage();
      trigger?.focus({ preventScroll: true });
    }, 230);
  };

  const refresh = () => {
    const config = getConfig();

    if (!config) {
      return;
    }

    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      if (config.bookingUrl) {
        trigger.href = config.bookingUrl;
      }

      trigger.removeAttribute('target');
      trigger.removeAttribute('rel');
      trigger.removeAttribute('aria-haspopup');
      trigger.removeAttribute('aria-controls');

      if (config.mode === 'new_tab') {
        trigger.target = '_blank';
        trigger.rel = 'noopener noreferrer';
      } else if (config.mode === 'modal') {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', config.controller.id);
      }
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(triggerSelector);

    if (trigger) {
      const config = getConfig();

      if (config?.mode === 'modal') {
        event.preventDefault();
        openModal(config.controller, trigger);
      }

      return;
    }

    if (activeModal && event.target.closest?.('[data-teams-demo-close]')) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(activeModal);

    if (!focusableElements.length) {
      event.preventDefault();
      activeModal.querySelector('.teams-demo-modal__panel')?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', (event) => {
    if (activeModal && event.target.contains(activeModal)) {
      activeModal.hidden = true;
      activeModal = null;
      activeTrigger = null;
      unlockPage();
    }

    window.requestAnimationFrame(refresh);
  });

  window.TeamsDemoModal = { refresh };
  refresh();
})();
